import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { allShapes, readPresentation, type ShapeInfo } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

/** Formen, die einen Alternativtext brauchen: alles Bildhafte. */
function bildhafteFormen(shapes: ShapeInfo[]): ShapeInfo[] {
  return shapes.filter(
    (form) =>
      form.type === "Image" ||
      form.type === "Chart" ||
      form.type === "SmartArt" ||
      form.type === "Diagram" ||
      form.type === "Media" ||
      form.type === "Model3D" ||
      form.type === "Ink" ||
      form.placeholder?.containedType === "Image" ||
      form.placeholder?.containedType === "Chart",
  );
}

/**
 * Ein brauchbarer Alternativtext beschreibt den Inhalt. Ein Dateiname oder ein
 * einzelnes Wort erfüllt diesen Zweck nicht – dieselbe Prüfung wie im
 * Word-Kurs.
 */
function istAussagekraeftig(text: string): boolean {
  if (text.length < 10) return false;
  if (/\.(png|jpe?g|gif|bmp|tiff?|webp|svg|emf)$/i.test(text)) return false;
  if (/^(bild|foto|grafik|image|screenshot|diagramm|chart)\s*\d*$/i.test(text)) return false;
  return text.split(/\s+/).length >= 2;
}

export const lektionAlternativtexte: Lesson = {
  id: "alternativtexte",
  title: "Alternativtexte",
  summary: "Was ein Bild zeigt – für alle, die es nicht sehen.",
  durationMinutes: 10,
  learningGoals: [
    "Alternativtexte für Bilder, Diagramme und SmartArt setzen",
    "Einen brauchbaren Alternativtext von einem nutzlosen unterscheiden",
    "Rein dekorative Objekte als solche kennzeichnen",
  ],
  steps: [
    {
      id: "was-ist-ein-alt-text",
      title: "Wofür ein Alternativtext da ist",
      explanation: [
        "Ein Screenreader liest eine Folie vor: den Titel, dann den Inhalt, dann die " +
          "übrigen Objekte. Bei einem Bild kann er nur vorlesen, was jemand über das " +
          "Bild geschrieben hat. Steht dort nichts, sagt er „Grafik“ – und die " +
          "Information ist weg.",
        "Ein Alternativtext beschreibt deshalb nicht das Bild, sondern seine Aussage. " +
          "Bei einem Foto einer Fabrikhalle ist „Fabrikhalle mit Fertigungsstraße“ " +
          "richtig, wenn es um die Halle geht. Geht es darum, dass die Produktion läuft, " +
          "ist „Laufende Fertigungsstraße im Werk Nord“ besser.",
        "Bei einem Diagramm ist der Alternativtext das Ergebnis, nicht die Bauart: " +
          "„Säulendiagramm mit vier Säulen“ hilft niemandem. „Umsatz steigt von 2022 bis " +
          "2025 von 4 auf 7 Mio. €“ schon.",
        "Ein guter Test: Lies den Alternativtext laut vor, ohne das Bild zu sehen. Weißt " +
          "du danach, worum es geht?",
      ],
      keyPoints: [
        "Rechtsklick auf das Objekt → „Alternativtext bearbeiten“.",
        "Die Aussage beschreiben, nicht die Bauart des Bildes.",
        "Bei Diagrammen gehört das Ergebnis in den Alternativtext.",
      ],
      task: {
        instruction:
          "Gib allen Bildern, Diagrammen und Grafiken deiner Präsentation einen " +
          "Alternativtext – oder markiere sie als dekorativ, wenn sie keine Information " +
          "tragen.",
        hint: "Rechtsklick auf das Objekt → „Alternativtext bearbeiten“. Der Bereich öffnet sich rechts.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.altText) {
            return nichtPruefbar(
              "Der Alternativtext einer Form",
              "1.10",
              "Öffne „Überprüfen“ → „Barrierefreiheit überprüfen“; fehlende Alternativtexte stehen dort unter „Fehlender Alternativtext“.",
            );
          }

          const objekte = bildhafteFormen(allShapes(snapshot));
          if (objekte.length === 0) {
            return fail(
              "Es gibt noch kein Bild, Diagramm oder SmartArt-Objekt in der Präsentation.",
              "Füge zuerst ein Bild oder Diagramm ein – die Module davor zeigen, wie.",
            );
          }

          const ohne = objekte.filter(
            (form) => (form.altText ?? "").trim().length === 0 && form.isDecorative !== true,
          );

          if (ohne.length === 0) {
            const dekorativ = objekte.filter((form) => form.isDecorative === true).length;
            return pass(
              `Alle ${objekte.length} Objekte sind versorgt` +
                (dekorativ > 0 ? ` (davon ${dekorativ} als dekorativ gekennzeichnet)` : "") +
                " – ein Screenreader kann die Präsentation vollständig wiedergeben.",
            );
          }

          return fail(
            `${ohne.length === 1 ? "Ein Objekt hat" : `${ohne.length} Objekte haben`} weder Alternativtext noch die Kennzeichnung „dekorativ“, ` +
              `zum Beispiel „${ohne[0].name ?? "unbenannt"}“ auf Folie ${ohne[0].slideIndex + 1}.`,
            "Rechtsklick auf das Objekt → „Alternativtext bearbeiten“. Trägt es keine Information, das Häkchen „Als dekorativ markieren“ setzen.",
          );
        },
      },
    },
    {
      id: "brauchbar",
      title: "Brauchbar statt nur vorhanden",
      explanation: [
        "Ein leeres Feld auszufüllen ist leicht; es sinnvoll auszufüllen ist die " +
          "eigentliche Arbeit. Drei Muster sind verbreitet und alle drei nutzlos: der " +
          "Dateiname („IMG_2481.jpg“), die Gattung („Bild“, „Diagramm“) und die " +
          "Wiederholung des Folientitels.",
        "PowerPoint bietet inzwischen an, einen Alternativtext automatisch zu erzeugen. " +
          "Das Ergebnis ist ein brauchbarer Ausgangspunkt und selten die richtige Antwort, " +
          "weil die Automatik das Bild beschreibt, nicht seine Rolle im Vortrag. " +
          "Nachbessern lohnt sich fast immer.",
        "Zur Länge: Ein bis zwei Sätze reichen. Alles darüber wird beim Vorlesen zur " +
          "Geduldsprobe. Wenn ein Diagramm wirklich mehr Erklärung braucht, gehört sie " +
          "als sichtbarer Text auf die Folie oder in die Sprechernotizen – dort " +
          "profitieren alle davon.",
        "Der Sonderfall dekorativ: Eine geschwungene Linie, ein Farbbalken, ein " +
          "Hintergrundmuster tragen keine Information. Für sie gibt es im " +
          "Alternativtext-Bereich das Häkchen „Als dekorativ markieren“. Ein Screenreader " +
          "überspringt sie dann vollständig – besser, als sie mit einer Notlösung zu " +
          "beschreiben.",
      ],
      keyPoints: [
        "Kein Dateiname, keine Gattung, keine Wiederholung des Titels.",
        "Ein bis zwei Sätze genügen.",
        "„Als dekorativ markieren“ für Objekte ohne Informationsgehalt.",
      ],
      task: {
        instruction:
          "Sieh deine Alternativtexte durch: Jeder soll beschreiben, was zu sehen ist – " +
          "kein Dateiname und kein einzelnes Wort.",
        hint: "Der Test: Ergibt der Text allein vorgelesen einen Sinn?",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.altText) {
            return nichtPruefbar(
              "Der Alternativtext einer Form",
              "1.10",
              "Lies deine Alternativtexte selbst durch – ergibt jeder allein vorgelesen einen Sinn?",
            );
          }

          const mitText = bildhafteFormen(allShapes(snapshot)).filter(
            (form) => (form.altText ?? "").trim().length > 0,
          );
          if (mitText.length === 0) {
            return fail(
              "Es ist noch kein Alternativtext gesetzt.",
              "Erledige zuerst den vorigen Schritt.",
            );
          }

          const duenn = mitText.filter((form) => !istAussagekraeftig((form.altText ?? "").trim()));
          if (duenn.length === 0) {
            return pass(
              `Alle ${mitText.length} Alternativtexte beschreiben tatsächlich etwas, zum Beispiel „${(mitText[0].altText ?? "").slice(0, 60)}“.`,
            );
          }

          return fail(
            `Auf Folie ${duenn[0].slideIndex + 1} lautet der Alternativtext „${(duenn[0].altText ?? "").slice(0, 50)}“ – ` +
              "das ist ein Dateiname oder ein einzelnes Wort und hilft beim Vorlesen nicht weiter.",
            "Beschreibe in ein bis zwei Sätzen, was zu sehen ist und worauf es dabei ankommt.",
          );
        },
      },
    },
  ],
};
